import { useState, useCallback, useEffect, useMemo } from 'react';
import GraphCanvas from './GraphCanvas';
import EdgeDetailsSidebar from './EdgeDetailsSidebar';
import NodeDetailsSidebar from './NodeDetailsSidebar';
import ColorLegend from './ColorLegend';
import { useGraphData } from './hooks/useGraphData';
import { filterToDrillScope, parseSymbolSearchResults } from './utils/transformers';
import { useSession } from '../../../context/SessionContext';
import { apiClient } from '../../../api/client';
import LoadingSpinner from '../../common/LoadingSpinner';
import './GraphPanel.css';

const VIZ_TYPES = [
  { id: 'architecture', label: 'Architecture', icon: 'modules' },
  { id: 'callgraph', label: 'Call Graph', icon: 'calls' },
  { id: 'files', label: 'Files', icon: 'files' },
];

const ARCH_MODES = [
  { id: 'structure', label: 'Structure', description: 'Modules and boundaries' },
  { id: 'dependencies', label: 'Dependencies', description: 'Coupling and cycles' },
];

const COLOR_MODES = [
  { id: 'type', label: 'Type', description: 'Color by file type' },
  { id: 'complexity', label: 'Complexity', description: 'Color by code complexity' },
  { id: 'churn', label: 'Churn', description: 'Color by change frequency' },
  { id: 'loc', label: 'Size', description: 'Color by lines of code' },
];

/**
 * Get icon for visualization type
 */
function VizIcon({ type }) {
  switch (type) {
    case 'modules':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7"/>
          <rect x="14" y="3" width="7" height="7"/>
          <rect x="14" y="14" width="7" height="7"/>
          <rect x="3" y="14" width="7" height="7"/>
        </svg>
      );
    case 'calls':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="5" r="3"/>
          <circle cx="6" cy="19" r="3"/>
          <circle cx="18" cy="19" r="3"/>
          <line x1="12" y1="8" x2="6" y2="16"/>
          <line x1="12" y1="8" x2="18" y2="16"/>
        </svg>
      );
    case 'files':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
          <polyline points="13 2 13 9 20 9"/>
        </svg>
      );
    default:
      return null;
  }
}

/**
 * Breadcrumb navigation for drill-down
 */
function DrillBreadcrumb({ path, onNavigate, prefix = 'Architecture' }) {
  const segments = path ? path.split('/').filter(Boolean) : [];
  const crumbs = segments.map((segment, index) => ({
    label: segment,
    path: segments.slice(0, index + 1).join('/')
  }));

  return (
    <div className="graph-breadcrumb drill-breadcrumb">
      <button
        className="breadcrumb-item breadcrumb-root"
        onClick={() => onNavigate(null)}
        title="Back to overview"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7"/>
          <rect x="14" y="3" width="7" height="7"/>
          <rect x="14" y="14" width="7" height="7"/>
          <rect x="3" y="14" width="7" height="7"/>
        </svg>
        <span>{prefix}</span>
      </button>
      {crumbs.map((crumb, index) => (
        <span key={crumb.path} className="breadcrumb-segment">
          <svg className="breadcrumb-separator" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
          <button
            className={`breadcrumb-item ${index === crumbs.length - 1 ? 'breadcrumb-current' : ''}`}
            onClick={() => onNavigate(crumb.path)}
            disabled={index === crumbs.length - 1}
          >
            {crumb.label}
          </button>
        </span>
      ))}
    </div>
  );
}

/**
 * Breadcrumb navigation for files view zoom (legacy)
 */
function Breadcrumb({ path, onNavigate }) {
  return <DrillBreadcrumb path={path} onNavigate={onNavigate} prefix="Root" />;
}

/**
 * Filter nodes and edges to show only a subtree starting from rootPath
 */
function filterToSubtree(nodes, edges, rootPath) {
  if (!rootPath) return { nodes, edges };

  // Find all nodes that are descendants of rootPath (including rootPath itself)
  const subtreeNodes = nodes.filter(node => {
    const nodePath = node.data?.path || node.id;
    return nodePath === rootPath || nodePath.startsWith(rootPath + '/');
  });

  const subtreeNodeIds = new Set(subtreeNodes.map(n => n.id));

  // Filter edges to only include those between subtree nodes
  const subtreeEdges = edges.filter(edge =>
    subtreeNodeIds.has(edge.source) && subtreeNodeIds.has(edge.target)
  );

  return { nodes: subtreeNodes, edges: subtreeEdges };
}

/**
 * Graph visualization panel
 */
export function GraphPanel({ selection, onNodeSelect, onImpactAnalysis, initialVizType }) {
  const [vizType, setVizType] = useState(initialVizType || 'architecture');
  const [archMode, setArchMode] = useState('structure'); // structure, dependencies
  const [targetSymbol, setTargetSymbol] = useState(null);
  const [subtreeRoot, setSubtreeRoot] = useState(null); // For filtering to subtree (files view)
  const [drillPath, setDrillPath] = useState(null); // For drill-down navigation (architecture view)
  const [colorMode, setColorMode] = useState('complexity'); // type, complexity, churn, loc
  const [searchQuery, setSearchQuery] = useState(''); // For filtering/highlighting nodes
  const [selectedEdge, setSelectedEdge] = useState(null); // For edge details sidebar
  const [selectedNode, setSelectedNode] = useState(null); // For node details sidebar

  // Call graph symbol search state
  const [callGraphSearch, setCallGraphSearch] = useState('');
  const [callGraphResults, setCallGraphResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const { sessionId } = useSession();

  // Dependency mode filters
  const [filters, setFilters] = useState({
    problemsOnly: false,
    cycles: false,
    boundaries: false,
    highFanIn: false,
  });

  // Entry point highlighting
  const [showEntryPoints, setShowEntryPoints] = useState(false);

  const { nodes: allNodes, edges: allEdges, loading, error, noDependencyData, refetch } = useGraphData(vizType, targetSymbol, archMode);

  // Detect if all file nodes came from file-tree fallback (no metrics available)
  const isFileTreeFallback = useMemo(() => {
    return allNodes.length > 0 && allNodes.every(n => n.data?.isFileTreeFallback);
  }, [allNodes]);

  // Effective color mode: lock to 'type' when using file-tree fallback
  const effectiveColorMode = isFileTreeFallback ? 'type' : colorMode;

  // For architecture mode: apply drill-down filtering to show direct children
  const { nodes: drillNodes, edges: drillEdges } = useMemo(() => {
    if (vizType !== 'architecture') {
      return { nodes: allNodes, edges: allEdges };
    }
    // Always apply drill scope filtering (even at root level)
    return filterToDrillScope(allNodes, allEdges, drillPath, allNodes);
  }, [allNodes, allEdges, drillPath, vizType]);

  // Filter to subtree if a root is selected (for files view)
  const { nodes: subtreeNodes, edges: subtreeEdges } = useMemo(() => {
    if (vizType === 'architecture') {
      return { nodes: drillNodes, edges: drillEdges };
    }
    if (subtreeRoot) {
      return filterToSubtree(allNodes, allEdges, subtreeRoot);
    }
    return { nodes: allNodes, edges: allEdges };
  }, [vizType, drillNodes, drillEdges, allNodes, allEdges, subtreeRoot]);

  // Calculate filter statistics for Dependencies mode
  const filterStats = useMemo(() => {
    if (archMode !== 'dependencies' || vizType !== 'architecture') {
      return { problemCount: 0, cycleCount: 0, highFanInCount: 0 };
    }

    const problemCount = subtreeNodes.filter(n =>
      n.data.coupling?.inCycle ||
      ['high', 'critical'].includes(n.data.coupling?.level)
    ).length;

    const cycleCount = subtreeNodes.filter(n => n.data.coupling?.inCycle).length;

    const highFanInCount = subtreeNodes.filter(n =>
      (n.data.coupling?.fanIn || 0) >= 4
    ).length;

    return { problemCount, cycleCount, highFanInCount };
  }, [subtreeNodes, archMode, vizType]);

  // Count entry points in current view
  const entryPointCount = useMemo(() => {
    return subtreeNodes.filter(n =>
      n.data.role === 'entrypoint' || n.data.boundary?.layer === 'entrypoint'
    ).length;
  }, [subtreeNodes]);

  // Apply dependency filters and entry point highlighting
  const { nodes, edges } = useMemo(() => {
    let filteredNodes = subtreeNodes;
    let filteredEdges = subtreeEdges;

    // Apply dependency mode filters
    if (archMode === 'dependencies' && vizType === 'architecture') {
      const hasActiveFilter = filters.problemsOnly || filters.cycles || filters.highFanIn || filters.boundaries;

      if (hasActiveFilter) {
        // Problems Only: hide nodes without cycles or high coupling
        if (filters.problemsOnly) {
          filteredNodes = subtreeNodes.filter(n =>
            n.data.coupling?.inCycle ||
            ['high', 'critical'].includes(n.data.coupling?.level)
          );
        }

        // Apply highlight flags to nodes
        filteredNodes = filteredNodes.map(n => ({
          ...n,
          data: {
            ...n.data,
            cycleHighlight: filters.cycles && n.data.coupling?.inCycle,
            fanInHighlight: filters.highFanIn && (n.data.coupling?.fanIn || 0) >= 4,
            boundaryMode: filters.boundaries,
          }
        }));

        // Filter edges to only those connecting visible nodes
        const nodeIds = new Set(filteredNodes.map(n => n.id));
        filteredEdges = subtreeEdges.filter(e =>
          nodeIds.has(e.source) && nodeIds.has(e.target)
        );

        // Apply highlight flags to edges
        filteredEdges = filteredEdges.map(e => ({
          ...e,
          data: {
            ...e.data,
            cycleHighlight: filters.cycles && e.data?.isCycle,
            crossBoundaryHighlight: filters.boundaries && e.data?.isCrossBoundary,
          }
        }));
      }
    }

    // Apply entry point highlighting (works in any mode)
    if (showEntryPoints) {
      filteredNodes = filteredNodes.map(n => ({
        ...n,
        data: {
          ...n.data,
          entryPointHighlight: n.data.role === 'entrypoint' || n.data.boundary?.layer === 'entrypoint',
        }
      }));
    }

    return { nodes: filteredNodes, edges: filteredEdges };
  }, [subtreeNodes, subtreeEdges, filters, archMode, vizType, showEntryPoints]);

  // When a symbol is selected, set it as target for call graph
  useEffect(() => {
    if (selection?.type === 'symbol' && selection.symbol?.id) {
      setTargetSymbol(selection.symbol.id);
    }
  }, [selection]);

  // Clear subtree/drill when switching viz types
  useEffect(() => {
    setSubtreeRoot(null);
    setDrillPath(null);
  }, [vizType]);

  // Clear filters and selections when switching modes
  useEffect(() => {
    if (archMode !== 'dependencies') {
      setFilters({ problemsOnly: false, cycles: false, boundaries: false, highFanIn: false });
    }
    setSelectedEdge(null);
    setSelectedNode(null);
  }, [archMode, vizType]);

  // Toggle a single filter
  const toggleFilter = useCallback((filterName) => {
    setFilters(prev => ({ ...prev, [filterName]: !prev[filterName] }));
  }, []);

  // Reset all filters, search, and drill-down
  const handleResetView = useCallback(() => {
    setFilters({ problemsOnly: false, cycles: false, boundaries: false, highFanIn: false });
    setSearchQuery('');
    setSelectedEdge(null);
    setSelectedNode(null);
    setDrillPath(null);
  }, []);

  // Handle edge click (for sidebar)
  const handleEdgeClick = useCallback((edgeData) => {
    setSelectedEdge(edgeData);
    setSelectedNode(null); // Clear node selection
  }, []);

  // Handle node double-click (drill-down)
  const handleNodeDoubleClick = useCallback((nodeData) => {
    // Only drill in architecture mode
    if (vizType !== 'architecture') return;

    // Only drill into nodes that have children
    if (!nodeData.hasChildren) return;

    // Don't drill into external nodes
    if (nodeData.path?.startsWith('__external')) return;

    // Drill down into the clicked node
    setDrillPath(nodeData.path);
    setSelectedEdge(null);
    setSelectedNode(null);
  }, [vizType]);

  // Handle node click - zoom into subtree for files view, navigate for others
  const handleNodeClick = useCallback((nodeData) => {
    // In files mode, clicking a directory zooms into its subtree
    if (vizType === 'files' && nodeData.path) {
      // Check if this node has children (is not a leaf)
      const hasChildren = allNodes.some(n => {
        const nPath = n.data?.path || n.id;
        return nPath !== nodeData.path && nPath.startsWith(nodeData.path + '/');
      });

      if (hasChildren) {
        setSubtreeRoot(nodeData.path);
      }
      // For leaf directories in files mode, do nothing (don't navigate away)
      return;
    }

    // In architecture mode, handle different node types
    if (vizType === 'architecture') {
      // Handle external node clicks - navigate to the external module
      if (nodeData.type === 'external' || nodeData.id?.startsWith('__external')) {
        // Find the external node to get its paths
        const externalNode = nodes.find(n => n.id === nodeData.id);
        const externalPaths = externalNode?.data?.externalPaths || [];

        if (externalPaths.length > 0) {
          // Navigate to the first external path's parent scope
          const targetPath = externalPaths[0].path;
          const parentPath = targetPath.includes('/')
            ? targetPath.split('/').slice(0, -1).join('/')
            : null;
          setDrillPath(parentPath);
          setSelectedNode(null);
          setSelectedEdge(null);
        }
        return;
      }

      // Regular node - show details sidebar
      const fullNode = allNodes.find(n =>
        (n.data?.path || n.id) === nodeData.path || n.id === nodeData.id
      );
      if (fullNode) {
        setSelectedNode(fullNode);
        setSelectedEdge(null); // Clear edge selection
      }
      return;
    }

    // For other viz types, navigate
    if (!onNodeSelect) return;

    if (nodeData.type === 'file' && nodeData.path) {
      onNodeSelect({
        type: 'file',
        path: nodeData.path,
        name: nodeData.name || nodeData.fileName,
      });
    } else if (nodeData.type === 'function' && nodeData.id) {
      onNodeSelect({
        type: 'symbol',
        symbol: {
          id: nodeData.id,
          name: nodeData.name,
        }
      });
    } else if (nodeData.type === 'module' && nodeData.path) {
      onNodeSelect({
        type: 'explore',
        path: nodeData.path,
        name: nodeData.name,
      });
    }
  }, [onNodeSelect, vizType, allNodes]);

  // Handle call graph symbol search
  const handleCallGraphSearch = useCallback(async (e) => {
    e.preventDefault();
    if (!callGraphSearch.trim() || !sessionId) return;

    setSearching(true);
    setCallGraphResults([]);
    try {
      const result = await apiClient.searchSymbols(sessionId, callGraphSearch.trim(), 10);
      const symbols = parseSymbolSearchResults(result);
      setCallGraphResults(symbols);
    } catch (err) {
      console.error('Symbol search failed:', err);
    } finally {
      setSearching(false);
    }
  }, [callGraphSearch, sessionId]);

  const renderEmptyState = () => {
    if (vizType === 'callgraph' && !targetSymbol) {
      return (
        <div className="graph-empty-state">
          <div className="empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="5" r="3"/>
              <circle cx="6" cy="19" r="3"/>
              <circle cx="18" cy="19" r="3"/>
              <line x1="12" y1="8" x2="6" y2="16"/>
              <line x1="12" y1="8" x2="18" y2="16"/>
            </svg>
          </div>
          <p className="empty-title">Search for a symbol to view its call graph</p>
          <div className="callgraph-search">
            <form className="callgraph-search-form" onSubmit={handleCallGraphSearch}>
              <input
                type="text"
                placeholder="Search functions, methods, classes..."
                value={callGraphSearch}
                onChange={(e) => setCallGraphSearch(e.target.value)}
                className="callgraph-search-input"
              />
              <button type="submit" disabled={searching || !callGraphSearch.trim()} className="callgraph-search-btn">
                {searching ? 'Searching...' : 'Search'}
              </button>
            </form>
            {callGraphResults.length > 0 && (
              <div className="callgraph-results">
                {callGraphResults.map((sym, i) => (
                  <button
                    key={sym.id || i}
                    className="callgraph-result-item"
                    onClick={() => {
                      setTargetSymbol(sym.id);
                      setCallGraphResults([]);
                      setCallGraphSearch('');
                    }}
                  >
                    <span className="result-name">{sym.name}</span>
                    <span className="result-kind">{sym.kind}</span>
                    {sym.file && <span className="result-file">{sym.file}</span>}
                  </button>
                ))}
              </div>
            )}
            {searching && (
              <div className="callgraph-searching">
                <LoadingSpinner text="Searching symbols..." />
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="graph-empty-state">
        <div className="empty-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="7" height="7"/>
            <rect x="14" y="3" width="7" height="7"/>
            <rect x="14" y="14" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/>
          </svg>
        </div>
        <p className="empty-title">No graph data available</p>
        <p className="empty-hint">
          The codebase analysis did not return any modules or dependencies to visualize.
        </p>
        <button className="retry-button" onClick={refetch}>
          Retry
        </button>
      </div>
    );
  };

  return (
    <div className="graph-panel">
      <div className="graph-controls">
        <div className="viz-selector">
          {VIZ_TYPES.map((type) => (
            <button
              key={type.id}
              className={`viz-option ${vizType === type.id ? 'active' : ''}`}
              onClick={() => setVizType(type.id)}
            >
              <VizIcon type={type.icon} />
              <span>{type.label}</span>
            </button>
          ))}
        </div>

        {vizType === 'architecture' && (
          <div className="arch-mode-selector">
            {ARCH_MODES.map((mode) => (
              <button
                key={mode.id}
                className={`arch-mode-option ${archMode === mode.id ? 'active' : ''}`}
                onClick={() => setArchMode(mode.id)}
                title={mode.description}
              >
                {mode.label}
              </button>
            ))}
          </div>
        )}

        {vizType === 'architecture' && archMode === 'dependencies' && (
          <div className="dep-filters">
            <label className={`filter-checkbox ${filters.problemsOnly ? 'active' : ''}`}>
              <input
                type="checkbox"
                checked={filters.problemsOnly}
                onChange={() => toggleFilter('problemsOnly')}
              />
              <span>Problems Only</span>
              {filterStats.problemCount > 0 && (
                <span className="filter-count">{filterStats.problemCount}</span>
              )}
            </label>
            <label className={`filter-checkbox ${filters.cycles ? 'active' : ''}`}>
              <input
                type="checkbox"
                checked={filters.cycles}
                onChange={() => toggleFilter('cycles')}
              />
              <span>Cycles</span>
              {filterStats.cycleCount > 0 && (
                <span className="filter-count">{filterStats.cycleCount}</span>
              )}
            </label>
            <label className={`filter-checkbox ${filters.boundaries ? 'active' : ''}`}>
              <input
                type="checkbox"
                checked={filters.boundaries}
                onChange={() => toggleFilter('boundaries')}
              />
              <span>Boundaries</span>
            </label>
            <label className={`filter-checkbox ${filters.highFanIn ? 'active' : ''}`}>
              <input
                type="checkbox"
                checked={filters.highFanIn}
                onChange={() => toggleFilter('highFanIn')}
              />
              <span>High Fan-In</span>
              {filterStats.highFanInCount > 0 && (
                <span className="filter-count">{filterStats.highFanInCount}</span>
              )}
            </label>
          </div>
        )}

        {vizType === 'callgraph' && targetSymbol && (
          <div className="target-indicator">
            <span className="target-label">Showing:</span>
            <span className="target-symbol">{targetSymbol}</span>
            <button
              className="clear-target"
              onClick={() => setTargetSymbol(null)}
              title="Clear selection"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        )}

        {vizType === 'files' && subtreeRoot && (
          <Breadcrumb path={subtreeRoot} onNavigate={setSubtreeRoot} />
        )}

        {vizType === 'architecture' && drillPath && (
          <DrillBreadcrumb
            path={drillPath}
            onNavigate={setDrillPath}
            prefix={archMode === 'structure' ? 'Structure' : 'Dependencies'}
          />
        )}

        {vizType === 'files' && (
          <div className="color-mode-selector">
            <span className="color-mode-label">Color:</span>
            <select
              value={effectiveColorMode}
              onChange={(e) => setColorMode(e.target.value)}
              className="color-mode-select"
              disabled={isFileTreeFallback}
              title={isFileTreeFallback ? 'Metric-based coloring requires CKB directory analysis' : ''}
            >
              {isFileTreeFallback
                ? <option value="type">Type</option>
                : COLOR_MODES.map((mode) => (
                    <option key={mode.id} value={mode.id} title={mode.description}>
                      {mode.label}
                    </option>
                  ))
              }
            </select>
          </div>
        )}

        <div className="graph-search">
          <svg className="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Filter nodes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="search-clear"
              onClick={() => setSearchQuery('')}
              title="Clear search"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          )}
        </div>

        {vizType === 'architecture' && (
          <button
            className={`entry-points-button ${showEntryPoints ? 'active' : ''}`}
            onClick={() => setShowEntryPoints(!showEntryPoints)}
            title="Highlight entry points (main files, index.js, etc.)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="12 2 2 7 12 12 22 7 12 2"/>
              <polyline points="2 17 12 22 22 17"/>
              <polyline points="2 12 12 17 22 12"/>
            </svg>
            Entry Points
            {entryPointCount > 0 && (
              <span className="entry-count">{entryPointCount}</span>
            )}
          </button>
        )}

        {vizType === 'architecture' && archMode === 'dependencies' && (
          <button
            className="reset-view-button"
            onClick={handleResetView}
            title="Reset filters and search"
          >
            Reset View
          </button>
        )}

        <button className="refresh-button" onClick={refetch} disabled={loading} title="Refresh">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23 4 23 10 17 10"/>
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
          </svg>
        </button>
      </div>

      <div className="graph-canvas-wrapper">
        {loading && (
          <div className="graph-loading">
            <LoadingSpinner text="Building graph..." />
          </div>
        )}

        {error && !loading && (
          <div className="graph-error">
            <div className="error-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <p className="error-message">{error}</p>
            <button className="retry-button" onClick={refetch}>
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && nodes.length === 0 && noDependencyData && (
          <div className="graph-empty-state">
            <div className="empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <p className="empty-title">Dependency data not available</p>
            <p className="empty-hint">
              Dependency analysis requires CKB import scanning which isn't ready yet.
              Try the Structure mode, or retry after CKB finishes indexing.
            </p>
            <button className="retry-button" onClick={refetch}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && nodes.length === 0 && !noDependencyData && renderEmptyState()}

        {!loading && !error && nodes.length > 0 && nodes.every(n => n.data?.isModuleFallback) && (
          <div className="graph-empty-state">
            <div className="empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <p className="empty-title">Directory-level data not available yet</p>
            <p className="empty-hint">
              CKB is still building its import analysis. The Files view requires directory-level data
              which becomes available after CKB finishes indexing. Try again shortly.
            </p>
            <button className="retry-button" onClick={refetch}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && nodes.length > 0 && !nodes.every(n => n.data?.isModuleFallback) && (
          <div className="graph-canvas-area">
            <GraphCanvas
              nodes={nodes}
              edges={edges}
              vizType={vizType}
              archMode={archMode}
              colorMode={effectiveColorMode}
              searchQuery={searchQuery}
              onNodeClick={handleNodeClick}
              onNodeDoubleClick={handleNodeDoubleClick}
              onEdgeClick={handleEdgeClick}
              selectedEdgeId={selectedEdge?.id}
            />
            {vizType === 'files' && (
              <ColorLegend colorMode={effectiveColorMode} />
            )}
          </div>
        )}

        {/* Edge Details Sidebar */}
        {selectedEdge && vizType === 'architecture' && (
          <EdgeDetailsSidebar
            edge={selectedEdge}
            onClose={() => setSelectedEdge(null)}
            onShowCallGraph={(edge) => {
              const symbols = edge.data?.symbols || [];
              if (symbols.length > 0) {
                setVizType('callgraph');
              }
              setSelectedEdge(null);
            }}
            onExploreModules={(node) => {
              if (node && onNodeSelect) {
                onNodeSelect({
                  type: 'explore',
                  path: node.data?.path,
                  name: node.data?.label,
                });
              }
              setSelectedEdge(null);
            }}
          />
        )}

        {/* Node Details Sidebar */}
        {selectedNode && vizType === 'architecture' && (
          <NodeDetailsSidebar
            node={selectedNode}
            edges={edges}
            onClose={() => setSelectedNode(null)}
            onExplore={(node) => {
              if (node && onNodeSelect) {
                onNodeSelect({
                  type: 'explore',
                  path: node.data?.path,
                  name: node.data?.label,
                });
              }
              setSelectedNode(null);
            }}
            onImpactAnalysis={(target) => {
              onImpactAnalysis?.(target);
              setSelectedNode(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

export default GraphPanel;
